import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../api/client';
import ArcaPageShell from './components/ArcaPageShell';
import ArcaFormularioBuilder from './components/ArcaFormularioBuilder';
import { STATUS_TEMPLATE_COLOR, VINCULO_TIPOS, TIPOS_SERVICO, OPCOES_CONFORMIDADE, calcPercentualConformidade, pontosDaResposta } from './utils/arcaFormConstants';
import { Link as RouterLink } from 'react-router-dom';

async function downloadExport(preenchimentoId, tipo, molduraId) {
  const qs = molduraId ? `?molduraId=${encodeURIComponent(molduraId)}` : '';
  const res = await apiFetch(`/api/arca/formularios/preenchimentos/${preenchimentoId}/export/${tipo}${qs}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Erro ao baixar ${tipo.toUpperCase()}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `formulario_${preenchimentoId}.${tipo === 'pdf' ? 'pdf' : 'docx'}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function UnidadeFilter({ value, onChange, unidades, isGlobal }) {
  return (
    <TextField
      select
      size="small"
      label="Unidade"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 200 }}
    >
      {isGlobal && <MenuItem value="TODAS">Todas as unidades</MenuItem>}
      {unidades.map((u) => (
        <MenuItem key={u.codigo} value={u.codigo}>
          {u.nome || u.codigo}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default function ArcaFormulariosPage() {
  const { unidadeSelecionada, isGlobalUser, user } = useAuth();
  const isGlobal = typeof isGlobalUser === 'function' ? isGlobalUser() : false;
  const [unidadeFiltro, setUnidadeFiltro] = useState(unidadeSelecionada?.codigo || '');
  const [unidades, setUnidades] = useState([]);
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [preenchimentos, setPreenchimentos] = useState([]);
  const [contexto, setContexto] = useState({ contratos: [], propostas: [], cadastros: [] });
  const [molduras, setMolduras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [exportReady, setExportReady] = useState(null); // { id, score, molduraId }

  const [novoOpen, setNovoOpen] = useState(false);
  const [tipoServicoFiltro, setTipoServicoFiltro] = useState('');
  const [novoForm, setNovoForm] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    vinculoTipo: 'CONTRATO',
    tipoServico: 'COZINHA',
    modoPontuacao: 'C_NC_NA',
    normaReferencia: ''
  });

  const [builderId, setBuilderId] = useState(null);
  const [preencherTpl, setPreencherTpl] = useState(null);
  const [preencherForm, setPreencherForm] = useState({
    titulo: '',
    vinculoTipo: 'CADASTRO_VISITA',
    contratoNumero: '',
    propostaId: '',
    cadastroId: '',
    molduraId: '',
    observacoes: '',
    respostas: {}
  });

  useEffect(() => {
    if (unidadeSelecionada?.codigo && !isGlobal) {
      setUnidadeFiltro(unidadeSelecionada.codigo);
    }
  }, [unidadeSelecionada?.codigo, isGlobal]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (unidadeFiltro) p.set('unidade', unidadeFiltro);
    if (tipoServicoFiltro) p.set('tipoServico', tipoServicoFiltro);
    return p.toString() ? `?${p.toString()}` : '';
  }, [unidadeFiltro, tipoServicoFiltro]);

  const scoreLive = useMemo(() => {
    if (!preencherTpl?.campos) return null;
    return calcPercentualConformidade(preencherForm.respostas, preencherTpl.campos);
  }, [preencherTpl, preencherForm.respostas]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const molduraQs = unidadeFiltro && unidadeFiltro !== 'TODAS'
        ? `?unidade=${encodeURIComponent(unidadeFiltro)}`
        : '';
      const [rU, rT, rP, rC, rM] = await Promise.all([
        apiFetch('/api/arca/unidades'),
        apiFetch(`/api/arca/formularios/templates${qs}`),
        apiFetch(`/api/arca/formularios/preenchimentos${qs}`),
        apiFetch(`/api/arca/formularios/contexto${qs}`),
        apiFetch(`/api/arca/formularios/molduras${molduraQs}`)
      ]);
      if (rU.ok) setUnidades(await rU.json());
      if (rT.ok) setTemplates(await rT.json());
      else {
        const err = await rT.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao carregar templates');
      }
      if (rP.ok) setPreenchimentos(await rP.json());
      if (rC.ok) setContexto(await rC.json());
      if (rM.ok) setMolduras(await rM.json());
    } catch (e) {
      setError(e.message || 'Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [qs, unidadeFiltro]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const criarTemplate = async () => {
    try {
      const res = await apiFetch('/api/arca/formularios/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...novoForm,
          unidade: unidadeFiltro === 'TODAS' ? unidadeSelecionada?.codigo : unidadeFiltro || null
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao criar');
      setNovoOpen(false);
      setNovoForm({
        codigo: '',
        nome: '',
        descricao: '',
        vinculoTipo: 'CONTRATO',
        tipoServico: 'COZINHA',
        modoPontuacao: 'C_NC_NA',
        normaReferencia: ''
      });
      setSnack('Template criado');
      await carregar();
      setBuilderId(data.id);
    } catch (e) {
      setError(e.message);
    }
  };

  const publicar = async (id) => {
    if (!window.confirm('Publicar este template?')) return;
    const res = await apiFetch(`/api/arca/formularios/templates/${id}/publicar`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Erro ao publicar');
      return;
    }
    setSnack('Template publicado');
    carregar();
  };

  const novaVersao = async (id) => {
    if (!window.confirm('Criar nova versão em rascunho?')) return;
    const res = await apiFetch(`/api/arca/formularios/templates/${id}/nova-versao`, { method: 'POST' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      setError(data.error || 'Erro');
      return;
    }
    setSnack(`Versão ${data.versao} criada`);
    carregar();
    setBuilderId(data.id);
  };

  const abrirPreencher = async (tpl) => {
    const res = await apiFetch(`/api/arca/formularios/templates/${tpl.id}`);
    const full = await res.json().catch(() => null);
    if (!res.ok || !full) {
      setError('Não foi possível abrir o template');
      return;
    }
    setPreencherTpl(full);
    const molduraPadrao = molduras.find((m) => m.escopo === 'UNIDADE') || molduras[0];
    setPreencherForm({
      titulo: `${full.nome} — ${new Date().toLocaleDateString('pt-BR')}`,
      vinculoTipo: full.vinculo_tipo || 'LIVRE',
      contratoNumero: '',
      propostaId: '',
      cadastroId: '',
      molduraId: molduraPadrao ? String(molduraPadrao.id) : '',
      observacoes: '',
      respostas: {}
    });
  };

  const salvarPreenchimento = async () => {
    try {
      const res = await apiFetch('/api/arca/formularios/preenchimentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateId: preencherTpl.id,
          titulo: preencherForm.titulo,
          vinculoTipo: preencherForm.vinculoTipo,
          contratoNumero: preencherForm.contratoNumero || null,
          propostaId: preencherForm.propostaId || null,
          cadastroId: preencherForm.cadastroId || null,
          respostas: preencherForm.respostas,
          observacoes: preencherForm.observacoes || null,
          molduraId: preencherForm.molduraId || null,
          unidade: unidadeFiltro === 'TODAS' ? unidadeSelecionada?.codigo : unidadeFiltro,
          inspetorNome: user?.nome,
          visitaStatus: preencherForm.vinculoTipo === 'CADASTRO_VISITA' ? 'REALIZADA' : null,
          status: 'CONCLUIDO'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      setPreencherTpl(null);
      setExportReady({
        id: data.id,
        score: data.score,
        molduraId: preencherForm.molduraId || null
      });
      setSnack(
        data.score?.percentual != null
          ? `Preenchimento registrado — conformidade ${data.score.percentual}%`
          : 'Preenchimento registrado'
      );
      setTab(1);
      carregar();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <ArcaPageShell
      title="Formulários"
      subtitle="Checklists por tipo de serviço (ex.: Cozinha RDC 216). Pontuação C=1 / NC=0 / NA=ignora → % de conformidade."
      maxWidth={1200}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <UnidadeFilter
          value={unidadeFiltro || (isGlobal ? 'TODAS' : '')}
          onChange={setUnidadeFiltro}
          unidades={
            unidades.length
              ? unidades
              : unidadeSelecionada
                ? [{ codigo: unidadeSelecionada.codigo, nome: unidadeSelecionada.nome }]
                : []
          }
          isGlobal={isGlobal}
        />
        <TextField
          select
          size="small"
          label="Tipo de serviço"
          value={tipoServicoFiltro}
          onChange={(e) => setTipoServicoFiltro(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {TIPOS_SERVICO.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={carregar}>
          Atualizar
        </Button>
        <Button component={RouterLink} to="/arca/formularios/configuracao" size="small">
          Molduras
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNovoOpen(true)}>
          Novo template
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {snack && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSnack('')}>
          {snack}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Templates" />
          <Tab label={`Preenchimentos (${preenchimentos.length})`} />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        <Table size="small" component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Serviço</TableCell>
              <TableCell>Vínculo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Campos</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Nenhum template nesta unidade
                </TableCell>
              </TableRow>
            ) : (
              templates.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.codigo}</TableCell>
                  <TableCell>
                    {t.nome}
                    <Typography variant="caption" display="block" color="text.secondary">
                      v{t.versao}
                      {t.unidade ? ` · ${t.unidade}` : ' · global'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {TIPOS_SERVICO.find((s) => s.value === t.tipo_servico)?.label || t.tipo_servico || '—'}
                  </TableCell>
                  <TableCell>
                    {VINCULO_TIPOS.find((v) => v.value === t.vinculo_tipo)?.label || t.vinculo_tipo}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t.status} color={STATUS_TEMPLATE_COLOR[t.status] || 'default'} />
                  </TableCell>
                  <TableCell>{t.campos_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t.status === 'PUBLICADO' ? 'Abrir / editar (nova versão)' : 'Builder'}>
                      <IconButton size="small" onClick={() => setBuilderId(t.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {t.status === 'RASCUNHO' && (
                      <Tooltip title="Publicar">
                        <IconButton size="small" color="success" onClick={() => publicar(t.id)}>
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {t.status === 'PUBLICADO' && (
                      <>
                        <Tooltip title="Nova versão">
                          <IconButton size="small" onClick={() => novaVersao(t.id)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Preencher">
                          <IconButton size="small" color="primary" onClick={() => abrirPreencher(t)}>
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      ) : (
        <Table size="small" component={Paper}>
          <TableHead>
            <TableRow>
              <TableCell>Título</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Vínculo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>% Conf.</TableCell>
              <TableCell>Visita</TableCell>
              <TableCell>Data</TableCell>
              <TableCell align="right">Exportar</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preenchimentos.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{p.titulo}</TableCell>
                <TableCell>{p.template_nome || p.template_id}</TableCell>
                <TableCell>
                  {p.vinculo_tipo}
                  {p.contrato_numero ? ` · ${p.contrato_numero}` : ''}
                  {p.cadastro_id ? ` · cad#${p.cadastro_id}` : ''}
                </TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell>
                  {p.percentual_conformidade != null ? `${p.percentual_conformidade}%` : '—'}
                </TableCell>
                <TableCell>{p.visita_status || '—'}</TableCell>
                <TableCell>{p.created_at ? String(p.created_at).slice(0, 16) : '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Baixar PDF">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        try {
                          await downloadExport(p.id, 'pdf', p.moldura_id);
                          setSnack('PDF baixado');
                        } catch (e) {
                          setError(e.message);
                        }
                      }}
                    >
                      <PictureAsPdfIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Baixar Word">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={async () => {
                        try {
                          await downloadExport(p.id, 'docx', p.moldura_id);
                          setSnack('Word baixado');
                        } catch (e) {
                          setError(e.message);
                        }
                      }}
                    >
                      <DescriptionIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={novoOpen} onClose={() => setNovoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo template</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Código"
              value={novoForm.codigo}
              onChange={(e) => setNovoForm((f) => ({ ...f, codigo: e.target.value.toUpperCase() }))}
              fullWidth
            />
            <TextField
              label="Nome"
              value={novoForm.nome}
              onChange={(e) => setNovoForm((f) => ({ ...f, nome: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Descrição"
              value={novoForm.descricao}
              onChange={(e) => setNovoForm((f) => ({ ...f, descricao: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Tipo de serviço"
              value={novoForm.tipoServico}
              onChange={(e) => setNovoForm((f) => ({ ...f, tipoServico: e.target.value }))}
              fullWidth
            >
              {TIPOS_SERVICO.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Tipo de vínculo"
              value={novoForm.vinculoTipo}
              onChange={(e) => setNovoForm((f) => ({ ...f, vinculoTipo: e.target.value }))}
              fullWidth
            >
              {VINCULO_TIPOS.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovoOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={criarTemplate} disabled={!novoForm.codigo || !novoForm.nome}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      <ArcaFormularioBuilder
        open={Boolean(builderId)}
        templateId={builderId}
        onClose={() => setBuilderId(null)}
        onSaved={carregar}
        onTemplateChanged={(id) => setBuilderId(id)}
      />

      <Dialog open={Boolean(preencherTpl)} onClose={() => setPreencherTpl(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Preencher — {preencherTpl?.nome}
          {scoreLive && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip
                color="primary"
                label={
                  scoreLive.percentual != null
                    ? `Conformidade: ${scoreLive.percentual}%`
                    : 'Conformidade: —'
                }
              />
              <Chip size="small" label={`C: ${scoreLive.conformes}`} color="success" variant="outlined" />
              <Chip size="small" label={`NC: ${scoreLive.naoConformes}`} color="error" variant="outlined" />
              <Chip size="small" label={`NA: ${scoreLive.naoAplicaveis}`} variant="outlined" />
            </Stack>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              Pontuação: <strong>Conforme = 1</strong>, <strong>Não conforme = 0</strong>, <strong>N/A = ignora</strong>.
              Média = % de conformidade sobre itens avaliados.
            </Alert>
            <TextField
              label="Título"
              value={preencherForm.titulo}
              onChange={(e) => setPreencherForm((f) => ({ ...f, titulo: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Vínculo"
              value={preencherForm.vinculoTipo}
              onChange={(e) => setPreencherForm((f) => ({ ...f, vinculoTipo: e.target.value }))}
              fullWidth
            >
              {VINCULO_TIPOS.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.label}
                </MenuItem>
              ))}
            </TextField>
            {preencherForm.vinculoTipo === 'CONTRATO' && (
              <TextField
                select
                label="Contrato"
                value={preencherForm.contratoNumero}
                onChange={(e) => setPreencherForm((f) => ({ ...f, contratoNumero: e.target.value }))}
                fullWidth
              >
                {(contexto.contratos || []).map((c) => (
                  <MenuItem key={c.id} value={c.numeroContrato}>
                    {c.numeroContrato} — {c.empresa}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {preencherForm.vinculoTipo === 'PROPOSTA' && (
              <TextField
                select
                label="Proposta"
                value={preencherForm.propostaId}
                onChange={(e) => setPreencherForm((f) => ({ ...f, propostaId: e.target.value }))}
                fullWidth
              >
                {(contexto.propostas || []).map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    #{p.id} {p.razao_social} ({p.edital_codigo || 'edital'})
                  </MenuItem>
                ))}
              </TextField>
            )}
            {preencherForm.vinculoTipo === 'CADASTRO_VISITA' && (
              <TextField
                select
                label="Cadastro / empresa (visita técnica)"
                value={preencherForm.cadastroId}
                onChange={(e) => setPreencherForm((f) => ({ ...f, cadastroId: e.target.value }))}
                fullWidth
              >
                {(contexto.cadastros || []).map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    #{c.id} {c.razao_social} — {c.status}
                    {c.visita_status ? ` · visita: ${c.visita_status}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              label="Moldura do documento"
              value={preencherForm.molduraId}
              onChange={(e) => setPreencherForm((f) => ({ ...f, molduraId: e.target.value }))}
              fullWidth
              helperText={
                molduras.length
                  ? 'PDF/Word usarão esta moldura (cores, cabeçalho e rodapé).'
                  : 'Nenhuma moldura cadastrada — o documento usará o padrão ARCA. Cadastre em Configuração.'
              }
            >
              <MenuItem value="">Padrão ARCA</MenuItem>
              {molduras.map((m) => (
                <MenuItem key={m.id} value={String(m.id)}>
                  {m.nome} ({m.escopo === 'CORPORATIVO' ? 'Corporativo' : m.unidade || 'Unidade'})
                </MenuItem>
              ))}
            </TextField>
            {(preencherTpl?.campos || []).map((campo) => {
              if (campo.tipo === 'SECAO') {
                return (
                  <Typography key={campo.id} variant="subtitle2" fontWeight={700} sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    {campo.label}
                  </Typography>
                );
              }
              const val = preencherForm.respostas[campo.chave] ?? '';
              const setVal = (v) =>
                setPreencherForm((f) => ({ ...f, respostas: { ...f.respostas, [campo.chave]: v } }));
              if (campo.tipo === 'SELECAO_UNICA') {
                const ops = Array.isArray(campo.opcoes) && campo.opcoes.length
                  ? campo.opcoes
                  : OPCOES_CONFORMIDADE;
                const pts = pontosDaResposta(val);
                const ptsLabel = val === '' ? '—' : pts === null ? 'NA' : String(pts);
                return (
                  <Stack key={campo.id} direction="row" spacing={1} alignItems="flex-start">
                    <Chip
                      size="small"
                      label={ptsLabel}
                      color={pts === 1 ? 'success' : pts === 0 ? 'error' : 'default'}
                      sx={{ mt: 1.5, minWidth: 44, fontWeight: 700 }}
                    />
                    <TextField
                      select
                      label={campo.label}
                      required={!!campo.obrigatorio}
                      value={val}
                      onChange={(e) => setVal(e.target.value)}
                      fullWidth
                    >
                      {ops.map((o) => {
                        const value = typeof o === 'object' ? o.value || o.label : o;
                        const label = typeof o === 'object' ? o.label || o.value : o;
                        const p = pontosDaResposta(value);
                        const suffix = p === null ? ' (ignora)' : ` (${p} pt)`;
                        return (
                          <MenuItem key={String(value)} value={value}>
                            {label}
                            {suffix}
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Stack>
                );
              }
              if (campo.tipo === 'BOOLEANO') {
                return (
                  <TextField
                    key={campo.id}
                    select
                    label={campo.label}
                    value={val === true || val === 'true' ? 'sim' : val === false || val === 'false' ? 'nao' : ''}
                    onChange={(e) => setVal(e.target.value === 'sim')}
                    fullWidth
                  >
                    <MenuItem value="sim">Sim</MenuItem>
                    <MenuItem value="nao">Não</MenuItem>
                  </TextField>
                );
              }
              if (campo.tipo === 'TEXTO_LONGO') {
                return (
                  <TextField
                    key={campo.id}
                    label={campo.label}
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                );
              }
              return (
                <TextField
                  key={campo.id}
                  label={campo.label}
                  type={campo.tipo === 'NUMERO' ? 'number' : campo.tipo === 'DATA' ? 'date' : 'text'}
                  value={val}
                  onChange={(e) => setVal(e.target.value)}
                  fullWidth
                  InputLabelProps={campo.tipo === 'DATA' ? { shrink: true } : undefined}
                />
              );
            })}
            <TextField
              label="Observações gerais"
              value={preencherForm.observacoes}
              onChange={(e) => setPreencherForm((f) => ({ ...f, observacoes: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
              helperText="Constará no PDF/Word junto com a nota e o espaço de assinatura."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreencherTpl(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvarPreenchimento}>
            Concluir e gerar documentos
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(exportReady)} onClose={() => setExportReady(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Download do formulário</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Preenchimento salvo
            {exportReady?.score?.percentual != null
              ? ` com conformidade de ${exportReady.score.percentual}%.`
              : '.'}{' '}
            Baixe o documento com a moldura aplicada:
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              color="error"
              startIcon={<PictureAsPdfIcon />}
              onClick={async () => {
                try {
                  await downloadExport(exportReady.id, 'pdf', exportReady.molduraId);
                  setSnack('PDF baixado');
                } catch (e) {
                  setError(e.message);
                }
              }}
            >
              Baixar PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              onClick={async () => {
                try {
                  await downloadExport(exportReady.id, 'docx', exportReady.molduraId);
                  setSnack('Word baixado');
                } catch (e) {
                  setError(e.message);
                }
              }}
            >
              Baixar Word
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportReady(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </ArcaPageShell>
  );
}
